import { computed, inject, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

import { BrowserStorageService } from '@spaces-ui/browser-storage';
import { BrowserWindowService } from '@spaces-ui/browser-window';
import { ENVIRONMENT } from '@spaces-ui/environment';
import { GlobalShortcutService } from '@spaces-ui/global-shortcuts';
import { MdwDialogService } from '@spaces-ui/meadow/components/dialog';
import { MdwNotificationsService } from '@spaces-ui/meadow/components/notifications';
import { ServiceBus } from '@spaces-ui/service-bus';
import { SystemUtils } from '@spaces-ui/utils';

import {
  DeepLink,
  ElectronBusEventPayloads,
  ElectronBusEvents,
  ElectronScreenShareMedia,
  MenuItem,
  parseDeepLink,
  SnapMediaStatus,
} from '../models';

type PendingDeepLink = { channel: 'authenticate' | 'deep-link'; payload: string };

export interface NavigationEntry {
  pageState?: string;
  title: string;
  url: string;
}

export interface EditableTargetHandlers {
  undo?: () => void;
  redo?: () => void;
  cut?: () => void;
  copy?: () => void;
  paste?: () => void;
  selectAll?: () => void;
}

declare const window: {
  electron: {
    setUnreadCounts: (unreadCounts: { unreadMentions: number; unreadMessages: number }) => void;
    quitAndInstall: () => void;
    focusWindow: () => void;
    restartApp: () => void;
    minimize: () => void;
    maximize: () => void;
    close: () => void;
    quit: () => void;
    clearAppData: () => void;
    downloadLogFile: () => void;
    getLogLevel: () => Promise<string>;
    setLogLevel: (level: string) => void;
    logDebug: (...args: unknown[]) => void;
    logInfo: (...args: unknown[]) => void;
    logWarn: (...args: unknown[]) => void;
    logError: (...args: unknown[]) => void;
    setSearchEnabled: (enabled: boolean) => void;
    sendNotification: (id: string, title: string, body: string, iconUrl?: string) => void;
    notificationClicked: (callback: (id: string) => void) => void;
    authenticate: (callback: (value: string) => void) => void;
    onDeepLink?: (callback: (url: string) => void) => void;
    getPendingDeepLinks?: () => Promise<PendingDeepLink[] | null>;
    onWindowVisibility?: (callback: (isVisible: boolean) => void) => void;
    screenShareSources: (callback: (sources: Array<ElectronScreenShareMedia>) => void) => void;
    screenShareSourceSelected: (sourceId: string | undefined) => void;
    toggleBlueGreenApi: (callback: () => void) => void;
    changeLogLevel: (callback: (newLogLevel: string) => void) => void;
    sendUpdateNotification: (callback: (newVersion: string) => void) => void;
    suspend: (callback: () => void) => void;
    resume: (callback: () => void) => void;
    lockScreen: (callback: () => void) => void;
    unlockScreen: (callback: () => void) => void;
    getAppName: () => Promise<string>;
    getOSPlatform: () => Promise<string>;
    getMenu: () => MenuItem[];
    onMenuAction: (callback: (action: string) => void) => void;
    performRole: (role: string) => void;
    readClipboard: () => Promise<{ text: string; html: string; imageDataUrl?: string }>;
    createChannel: (callback: () => void) => void;
    createDM: (callback: () => void) => void;
    getHistory: () => Promise<{ entries: NavigationEntry[]; activeIndex: number }>;
    goToHistoryIndex: (index: number) => Promise<void>;
    clearHistory: () => Promise<void>;
    callStart: () => Promise<void>;
    callEnd: () => Promise<void>;
    getSnapMediaStatus: () => Promise<SnapMediaStatus>;
    onSnapMediaStatus: (callback: (status: SnapMediaStatus) => void) => void;
    onContextMenuData: (
      callback: (data: { misspelledWord: string; suggestions: string[] }) => void,
    ) => void;
    replaceMisspelling: (suggestion: string) => void;
    addToDictionary: (word: string) => void;
    copyImageAt: (x: number, y: number) => void;
  };
  location: {
    reload: () => void;
  };
};

const TARGETS_BLUE_STORAGE_KEY = 'targets-blue-api';

@Injectable({ providedIn: 'root' })
export class ElectronServiceBus {
  private notificationService = inject(MdwNotificationsService);
  private env = inject(ENVIRONMENT);
  private browserWindowService = inject(BrowserWindowService);
  private globalShortcutService = inject(GlobalShortcutService);
  private dialogService = inject(MdwDialogService);
  private serviceBus = inject(ServiceBus);

  public authInfo = computed(() => this._authInfo());
  public get isElectronApp() {
    return !!window.electron;
  }
  public productName = this.env.productName;

  public targetsBlueApi = computed(() => this._targetsBlueApi());
  private _targetsBlueApi = signal<boolean>(false);

  public pendingUpdateVersion = computed(() => this._pendingUpdateVersion());
  private _pendingUpdateVersion = signal<string | undefined>(undefined);

  private _authInfo = signal<
    { token: string; trustedLogins: string; refreshToken: string } | undefined
  >(undefined);

  /** Latest `<scheme>://` deep link (other than authenticate) handed to us by the main process. */
  public deepLink = computed(() => this._deepLink());
  private _deepLink = signal<DeepLink | undefined>(undefined);

  public osPlatform = computed(() => this._osPlatform());
  private _osPlatform = signal<string | undefined>(undefined);

  private _lastFocussedElement = signal<HTMLElement | null>(null);

  private editableTargets = new Map<HTMLElement, EditableTargetHandlers>();

  public history = computed(() => this._history());
  private _history = signal<{ entries: NavigationEntry[]; activeIndex: number }>({
    entries: [],
    activeIndex: 0,
  });

  public logLevel = computed(() => this._logLevel());
  private _logLevel = signal<string | undefined>(undefined);

  public isSuspended = computed(() => this._isSuspended());
  public isLocked = computed(() => this._isLocked());
  private _isSuspended = signal(false);
  private _isLocked = signal(false);

  /** False while this renderer's window is minimized or hidden (Electron only; blur doesn't count). */
  public isWindowVisible = computed(() => this._isWindowVisible());
  private _isWindowVisible = signal(true);

  public isMacElectronApp = computed(() => {
    const osPlatform = this.osPlatform();
    return osPlatform ? osPlatform.toLowerCase().includes('darwin') : false;
  });

  public isWindowsElectronApp = computed(() => {
    const osPlatform = this.osPlatform();
    return osPlatform
      ? osPlatform.toLowerCase().includes('win') &&
          !osPlatform.toLocaleLowerCase().includes('darwin')
      : false;
  });

  public isLinuxElectronApp = computed(() => {
    const osPlatform = this.osPlatform();
    return osPlatform ? osPlatform.toLowerCase().includes('linux') : false;
  });

  public menuItems = computed(() => {
    const menuItems = this._menuItems();
    const osPlatform = this.osPlatform();
    const isSearchEnabled = this.isSearchEnabled();

    if (!menuItems || !osPlatform) {
      return [];
    }

    return this.formatAccelerators(menuItems, osPlatform, isSearchEnabled);
  });
  private _menuItems = signal<MenuItem[] | undefined>(undefined);

  private notificationClickHandlers: Record<string, () => void> = {};
  private isSearchEnabled = signal<boolean>(false);
  public snapMediaStatus = computed(() => this._snapMediaStatus());
  private _snapMediaStatus = signal<SnapMediaStatus | undefined>(undefined);

  private readonly _goBack$ = new Subject<void>();
  public readonly goBack$ = this._goBack$.asObservable();

  private readonly _goForward$ = new Subject<void>();
  public readonly goForward$ = this._goForward$.asObservable();

  public constructor() {
    if (window.electron) {
      this._targetsBlueApi.set(
        BrowserStorageService.getLocalStorageItem(TARGETS_BLUE_STORAGE_KEY) === 'true',
      );

      this.initPlatform();
      this.initLogLevel();
      this.getAppMenu();
      this.initFocussedElementListener();
      this.initNotificationProvider();
      this.initSnapMediaStatus();

      window.electron.onSnapMediaStatus(status => this._snapMediaStatus.set(status));

      window.electron.authenticate((value: string) => this.handleAuthenticateQuery(value));

      // Deep links: live pushes plus a one-time pull of anything that arrived before this renderer
      // had its listeners registered (cold start, reload).
      window.electron.onDeepLink?.(url => this.handleIncomingDeepLink(url));
      this.initPendingDeepLinks();

      window.electron.onWindowVisibility?.(isVisible => this._isWindowVisible.set(isVisible));

      // Events that come from electron:
      window.electron.toggleBlueGreenApi(() => {
        this.swapBlueGreenApi();
      });

      window.electron.changeLogLevel(newLogLevel => {
        this._logLevel.set(newLogLevel);
        this.notificationService.showSuccess(`Log level changed to ${newLogLevel}`);
      });

      window.electron.sendUpdateNotification(updateVersion => {
        this._pendingUpdateVersion.set(updateVersion);
      });

      window.electron.onMenuAction(action => this.handleMenuAction(action));

      window.electron.createChannel(() => this.openCreateChannelDialog());
      window.electron.createDM(() => this.openCreateDMDialog());

      window.electron.suspend(() => {
        this.logDebug('Power Monitor: System is suspending');
        this._isSuspended.set(true);
      });
      window.electron.resume(() => {
        this.logDebug('Power Monitor: System has resumed');
        this._isSuspended.set(false);
      });
      window.electron.lockScreen(() => {
        this.logDebug('Power Monitor: Screen is locked');
        this._isLocked.set(true);
      });
      window.electron.unlockScreen(() => {
        this.logDebug('Power Monitor: Screen is unlocked');
        this._isLocked.set(false);
      });

      window.electron.notificationClicked((id: string) => {
        const handler = this.notificationClickHandlers[id];
        if (handler) {
          handler();
          delete this.notificationClickHandlers[id];
        }
      });

      window.electron.screenShareSources((sources: Array<ElectronScreenShareMedia>) => {
        this.serviceBus.publish<
          ElectronBusEvents.ReceiveScreenShareSources,
          ElectronBusEventPayloads
        >(ElectronBusEvents.ReceiveScreenShareSources, sources);
      });
    }
  }

  public async refreshSnapMediaStatus() {
    if (!window.electron?.getSnapMediaStatus) {
      return;
    }

    try {
      const status = await window.electron.getSnapMediaStatus();
      this._snapMediaStatus.set(status);
    } catch (error) {
      console.warn('Failed to refresh snap media status', error);
    }
  }

  public async getHistory() {
    if (window.electron) {
      const history = await window.electron.getHistory();
      this._history.set(history);
    }
  }

  public async goToHistoryIndex(index: number) {
    if (window.electron) {
      await window.electron.goToHistoryIndex(index);
    }
  }

  public goBack() {
    this._goBack$.next();
  }

  public goForward() {
    this._goForward$.next();
  }

  public clearHistory() {
    if (window.electron) {
      window.electron.clearHistory();
    }
  }

  public callStart() {
    if (window.electron) {
      window.electron.callStart();
    }
  }

  public callEnd() {
    if (window.electron) {
      window.electron.callEnd();
    }
  }

  public swapBlueGreenApi() {
    this._targetsBlueApi.set(!this._targetsBlueApi());
    BrowserStorageService.setLocalStorageItem(
      TARGETS_BLUE_STORAGE_KEY,
      `${this._targetsBlueApi()}`,
    );
    this.notificationService.showSuccess(
      `Now Targeting ${this._targetsBlueApi() ? 'Blue' : 'Green'} API`,
    );
  }

  public clearDeepLink() {
    this._deepLink.set(undefined);
  }

  private handleAuthenticateQuery(value: string) {
    const token = new URLSearchParams(value).get('stytch_token');
    const trustedLogins = new URLSearchParams(value).get('trusted_logins');
    const refreshToken = new URLSearchParams(value).get('refresh_token');

    if (token && trustedLogins && refreshToken) {
      this._authInfo.set({ token, trustedLogins, refreshToken });
    }
  }

  private handleIncomingDeepLink(url: string) {
    const parsed = parseDeepLink(url, this.env.uri.electronProtocol);

    if (!parsed) {
      this.logWarn('Ignoring unrecognised deep link');
      return;
    }

    if (parsed.kind === 'Authenticate') {
      this.handleAuthenticateQuery(parsed.query);
      return;
    }

    this._deepLink.set(parsed);
  }

  private async initPendingDeepLinks() {
    if (!window.electron.getPendingDeepLinks) {
      // Older preload without the bridge (dev-only skew).
      return;
    }

    try {
      const pending = (await window.electron.getPendingDeepLinks()) ?? [];

      for (const item of pending) {
        if (item.channel === 'authenticate') {
          this.handleAuthenticateQuery(item.payload);
        } else {
          this.handleIncomingDeepLink(item.payload);
        }
      }
    } catch (error) {
      this.logError('Failed to read pending deep links', error);
    }
  }

  public clearAuthInfo() {
    this._authInfo.set(undefined);
  }

  public setUnreadCounts(unreadCounts: { unreadMentions: number; unreadMessages: number }) {
    if (window.electron) {
      window.electron.setUnreadCounts(unreadCounts);
    }
  }

  public setSearchEnabled(enabled: boolean) {
    if (window.electron) {
      this.isSearchEnabled.set(enabled);
      window.electron.setSearchEnabled(enabled);
    }
  }

  public quitAndInstallUpdate() {
    if (window.electron) {
      window.electron.quitAndInstall();
    }
  }

  public restartApp() {
    if (window.electron) {
      window.electron.restartApp();
    }
  }

  public focusWindow() {
    if (window.electron) {
      window.electron.focusWindow();
    }
  }

  public minimizeWindow() {
    if (window.electron) {
      window.electron.minimize();
    }
  }

  public maximizeWindow() {
    if (window.electron) {
      window.electron.maximize();
    }
  }

  public closeWindow() {
    if (window.electron) {
      window.electron.close();
    }
  }

  public logDebug(...args: unknown[]) {
    if (window.electron) {
      window.electron.logDebug(...args);
    }
  }

  public logInfo(...args: unknown[]) {
    if (window.electron) {
      window.electron.logInfo(...args);
    }
  }

  public logWarn(...args: unknown[]) {
    if (window.electron) {
      window.electron.logWarn(...args);
    }
  }

  // comes from ipcrenderer on mac
  // comes from app component on windows and linux
  public handleMenuAction(action: string) {
    if (!window.electron) {
      return;
    }

    switch (action) {
      case 'exit':
        window.electron.quit();
        break;
      case 'create-channel':
        this.openCreateChannelDialog();
        break;
      case 'create-dm':
        this.openCreateDMDialog();
        break;
      case 'minimize':
        window.electron.minimize();
        break;
      case 'maximize':
        window.electron.maximize();
        break;
      case 'reload':
        this.reloadData();
        break;
      case 'force-reload':
        this.restartApp();
        break;
      case 'download-logs':
        window.electron.downloadLogFile();
        break;
      case 'go-to':
        this.goTo();
        break;
      case 'all-organizations':
        this.allOrganizations();
        break;
      case 'toggle-todos':
        this.toggleTodos();
        break;
      case 'mark-all-read':
        this.markAllRead();
        break;
      case 'find':
        this.goToFind();
        break;
      case 'open-documentation':
        this.openDocumentation();
        break;
      case 'clear-app-data':
        this.clearAppData();
        break;
      case 'go-back':
        this.goBack();
        break;
      case 'go-forward':
        this.goForward();
        break;
      case 'undo':
      case 'redo':
      case 'cut':
      case 'copy':
      case 'paste':
        this.performRole(action);
        break;
      case 'select-all':
        this.performRole('selectAll');
        break;
    }
  }

  public performRole(role: string) {
    const handler = this.findEditableTargetHandler(role);
    if (handler) {
      handler();
      return;
    }

    if (window.electron) {
      const lastFocusedElement = this._lastFocussedElement();

      setTimeout(() => {
        lastFocusedElement?.focus();
        window.electron.performRole(role);
      });
    }
  }

  public registerEditableTarget(el: HTMLElement, handlers: EditableTargetHandlers): () => void {
    this.editableTargets.set(el, handlers);
    return () => {
      this.editableTargets.delete(el);
    };
  }

  private findEditableTargetHandler(role: string): (() => void) | undefined {
    const focused = this._lastFocussedElement();
    if (!focused) {
      return undefined;
    }

    for (const [el, handlers] of this.editableTargets) {
      if (el === focused || el.contains(focused)) {
        return handlers[role as keyof EditableTargetHandlers];
      }
    }

    return undefined;
  }

  public logError(...args: unknown[]) {
    if (window.electron) {
      window.electron.logError(...args);
    }
  }

  public selectScreenShareSource(sourceId: string | undefined) {
    if (window.electron) {
      window.electron.screenShareSourceSelected(sourceId);
    }
  }

  public onContextMenuData(
    callback: (data: { misspelledWord: string; suggestions: string[] }) => void,
  ) {
    if (window.electron) {
      window.electron.onContextMenuData(callback);
    }
  }

  public replaceMisspelling(suggestion: string) {
    if (window.electron) {
      window.electron.replaceMisspelling(suggestion);
    }
  }

  public addToDictionary(word: string) {
    if (window.electron) {
      window.electron.addToDictionary(word);
    }
  }

  public copyImageAt(x: number, y: number) {
    if (window.electron) {
      window.electron.copyImageAt(x, y);
    }
  }

  private async clearAppData() {
    if (window.electron) {
      this.dialogService.confirm(
        {
          title: 'Clear app data?',
          message: `This will reset Spaces on your device by clearing things like your theme, saved login, and last viewed channel.
          
It won’t affect your account or any of your messages. After clearing, the app will restart.`,
          dangerButton: 'Clear & Restart',
          primaryButton: undefined,
          messageIsSecondary: true,
        },
        () => {
          window.electron.clearAppData();
        },
      );
    }
  }

  private async initPlatform() {
    const platform = await window.electron.getOSPlatform();

    this._osPlatform.set(platform);
  }

  private async initLogLevel() {
    const logLevel = await window.electron.getLogLevel();

    this._logLevel.set(logLevel);
  }

  // Main answers with a change-log-level push, which updates the signal and toasts.
  public setLogLevel(level: string) {
    if (window.electron) {
      window.electron.setLogLevel(level);
    }
  }

  private reloadData() {
    this.browserWindowService.refreshPage();
  }

  private getAppMenu() {
    this._menuItems.set(window.electron.getMenu());
  }

  private formatAccelerators(
    items: MenuItem[],
    osPlatform: string,
    isSearchEnabled: boolean,
  ): MenuItem[] {
    const isMac = osPlatform.toLowerCase().includes('darwin');

    const recurse = (menu: MenuItem[]): MenuItem[] => {
      const menuItems = menu
        .map(item => {
          if (item.action === 'find' && !isSearchEnabled) {
            return undefined;
          }
          const newItem: MenuItem = {
            ...item,
            displayAccelerator: SystemUtils.formatKeyboardShortcut(item.accelerator, isMac),
          };
          if (item.submenu) {
            newItem.submenu = recurse(item.submenu);
          }
          return newItem;
        })
        .filter(item => !!item);

      if (menuItems.length > 0 && menuItems[menuItems.length - 1].type === 'separator') {
        menuItems.pop(); // Remove trailing separator
      }

      return menuItems;
    };

    return recurse(items);
  }

  private initFocussedElementListener() {
    document.addEventListener('focusin', event => {
      const target = event.target as HTMLElement;
      const notInFileMenu = !target.closest('.file-menu');
      const notAppMenuItem = !target.closest('.app-menu-item');
      const notInCdkMenu = !target.closest('[cdkMenu]');

      if (notInFileMenu && notAppMenuItem && notInCdkMenu) {
        this._lastFocussedElement.set(target);
      }
    });

    document.addEventListener('selectionchange', () => {
      const selection = document.getSelection();
      if (!selection || selection.isCollapsed) return;

      const anchorNode = selection.anchorNode;
      if (!anchorNode) return;

      const element =
        anchorNode.nodeType === Node.ELEMENT_NODE
          ? (anchorNode as HTMLElement)
          : anchorNode.parentElement;

      if (element) {
        const elementExists = document.contains(element);
        const notInFileMenu = !element.closest('.file-menu');
        const notAppMenuItem = !element.closest('.app-menu-item');
        const notInCdkMenu = !element.closest('[cdkMenu]');

        if (elementExists && notInFileMenu && notAppMenuItem && notInCdkMenu) {
          this._lastFocussedElement.set(element);
        }
      }
    });
  }

  private openCreateChannelDialog() {
    this.globalShortcutService.openCreateChannelDialog();
  }

  private openCreateDMDialog() {
    this.globalShortcutService.openCreateDMDialog();
  }

  private goTo() {
    this.globalShortcutService.goTo();
  }

  private allOrganizations() {
    this.globalShortcutService.openAllOrganizations();
  }

  private toggleTodos() {
    this.globalShortcutService.openTodos();
  }

  private markAllRead() {
    this.globalShortcutService.markAllRead();
  }

  private initNotificationProvider() {
    const createNotification = (
      id: string,
      title: string,
      body: string,
      onClick?: () => void,
      iconUrl?: string,
    ) => {
      if (onClick) {
        this.notificationClickHandlers[id] = onClick;
      }
      window.electron.sendNotification(id, title, body, iconUrl);
    };

    this.browserWindowService.registerNotificationOverride(createNotification);
  }

  private goToFind() {
    this.globalShortcutService.goToFind();
  }

  private openDocumentation() {
    this.globalShortcutService.openDocumentation();
  }

  private initSnapMediaStatus() {
    window.electron
      .getSnapMediaStatus()
      .then(status => this._snapMediaStatus.set(status))
      .catch(() => this._snapMediaStatus.set(undefined));
  }
}
